import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import { getUser, getAuth } from '../utils/auth';

interface Notification {
  id: string;
  type: 'NEW_SALE' | 'LOW_STOCK' | 'NEW_RESERVATION' | 'NEW_B2B_ACCESS_REQUEST';
  title: string;
  message: string;
  timestamp: string;
  data?: any;
}

interface NotificationsContextType {
  notifications: Notification[];
  unreadCount: number;
  markAsRead: (id: string) => void;
  clearAll: () => void;
  connected: boolean;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    const user = getUser();
    const auth = getAuth();
    // Connect if user has locationId OR organizationId (for org-level notifications)
    if (!user || !auth?.access_token || (!user.locationId && !user.organizationId)) {
      return;
    }

    // Connect to WebSocket with JWT authentication
    const newSocket = io('http://localhost:3001/notifications', {
      auth: {
        token: auth.access_token,
      },
      transports: ['websocket', 'polling'],
    });

    // Guard against null socket (e.g., in tests when mocked improperly)
    if (!newSocket) {
      return;
    }

    newSocket.on('connect', () => {
      console.log('WebSocket connected');
      setConnected(true);
    });

    newSocket.on('disconnect', () => {
      console.log('WebSocket disconnected');
      setConnected(false);
    });

    newSocket.on('error', (error: { message: string }) => {
      console.error('WebSocket authentication error:', error.message);
      setConnected(false);
    });

    newSocket.on('connect_error', (error: Error) => {
      console.error('WebSocket connection error:', error.message);
      setConnected(false);
    });

    // Location-level notifications (only if user has a locationId)
    if (user.locationId) {
      // Listen for new sale notifications
      newSocket.on(`location:${user.locationId}:new-sale`, (payload: any) => {
        console.log('New sale notification received:', payload);
        const notification: Notification = {
          id: Date.now().toString() + Math.random(),
          type: 'NEW_SALE',
          title: 'New Sale',
          message: `Receipt #${payload.data.receiptNumber} - ${formatCurrency(payload.data.total)}`,
          timestamp: payload.timestamp,
          data: payload.data,
        };
        setNotifications((prev) => [notification, ...prev]);

        // Play notification sound (optional)
        try {
          const audio = new Audio('/notification.mp3');
          audio.volume = 0.3;
          audio.play().catch(err => console.log('Could not play sound:', err));
        } catch (e) {
          console.log('Audio not supported');
        }
      });

      // Listen for low stock alerts
      newSocket.on(`location:${user.locationId}:low-stock`, (payload: any) => {
        console.log('Low stock alert received:', payload);
        const notification: Notification = {
          id: Date.now().toString() + Math.random(),
          type: 'LOW_STOCK',
          title: 'Low Stock Alert',
          message: `${payload.data.productName} is low on stock`,
          timestamp: payload.timestamp,
          data: payload.data,
        };
        setNotifications((prev) => [notification, ...prev]);
      });

      // Listen for new reservations
      newSocket.on(`location:${user.locationId}:new-reservation`, (payload: any) => {
        console.log('New reservation notification received:', payload);
        const notification: Notification = {
          id: Date.now().toString() + Math.random(),
          type: 'NEW_RESERVATION',
          title: 'New Reservation',
          message: `New reservation received`,
          timestamp: payload.timestamp,
          data: payload.data,
        };
        setNotifications((prev) => [notification, ...prev]);
      });
    }

    // Listen for new B2B access requests (organization-level)
    if (user.organizationId) {
      newSocket.on(`org:${user.organizationId}:new-b2b-access-request`, (payload: any) => {
        console.log('New B2B access request notification received:', payload);
        const notification: Notification = {
          id: Date.now().toString() + Math.random(),
          type: 'NEW_B2B_ACCESS_REQUEST',
          title: 'New B2B Access Request',
          message: `${payload.data.companyName} (${payload.data.firstName} ${payload.data.lastName}) has requested B2B access`,
          timestamp: payload.timestamp,
          data: payload.data,
        };
        setNotifications((prev) => [notification, ...prev]);

        // Play notification sound
        try {
          const audio = new Audio('/notification.mp3');
          audio.volume = 0.3;
          audio.play().catch(err => console.log('Could not play sound:', err));
        } catch (e) {
          console.log('Audio not supported');
        }
      });
    }

    return () => {
      newSocket.close();
    };
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
    }).format(amount);
  };

  const markAsRead = useCallback((id: string) => {
    setNotifications((prev) =>
      prev.map((notif) =>
        notif.id === id ? { ...notif, read: true } as any : notif
      )
    );
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  const unreadCount = notifications.filter((n: any) => !n.read).length;

  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        unreadCount,
        markAsRead,
        clearAll,
        connected,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationsProvider');
  }
  return context;
};
